/**
 * @fileoverview Tooltip component for displaying district information on map hover/selection.
 *
 * This component renders a tooltip that appears when hovering over or selecting
 * districts on the map.
 * It displays information about the hovered district including:
 * - District identifier (formatted as "State District N" when available)
 * - Legislator name
 * - Party affiliation
 * - Vote type (Yea, Nay, No Vote)
 * - Metric value and title (if a metric is selected)
 *
 * The tooltip is positioned based on the map tooltip position stored in the viz store
 * and appears when the active district comes from map interaction:
 * - A transient hover over a district, or
 * - A locked selection created by clicking a district on the map.
 *
 * Plot hovers do not display this tooltip.
 *
 * @see useVizStore - For hover state and tooltip position
 * @see useDataStore - For district data and metric information
 */

import { useMemo } from 'react';
import { useVizStore, useDataStore } from '@/store';
import { VoteType, type DistrictId } from '@/types';

/**
 * Converts a string to title case (first letter of each word capitalized).
 *
 * @param s - Optional string to convert
 * @returns Title-cased string, or empty string if input is undefined/null
 *
 * @example
 * toTitleCase('yea') // Returns 'Yea'
 * toTitleCase('no vote') // Returns 'No Vote'
 */
function toTitleCase(s?: string) {
  return (s ?? '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * TooltipContent component - Renders the content inside the tooltip.
 *
 * Displays district information in a two-column grid layout:
 * - Left column: Labels (Legislator, Party, Vote, Metric)
 * - Right column: Values (formatted with fallback to '—' if missing)
 *
 * @param props - Component props
 * @param props.districtId - District identifier
 * @param props.row - Optional district data row with legislator and vote information
 * @param props.row.legislatorName - Name of the legislator
 * @param props.row.partyName - Party affiliation
 * @param props.row.district - District number
 * @param props.row.stateName - State name
 * @param props.row.value - Vote type (Yea, Nay, No Vote)
 * @param props.metricTitle - Optional metric title to display
 * @param props.metricValue - Optional metric value to display
 *
 * @returns JSX element containing formatted tooltip content
 */
function TooltipContent({
  districtId,
  row,
  metricTitle,
  metricValue,
  useFeatureLabel,
  rank,
}: {
  districtId: string;
  row?: {
    legislatorId?: string;
    legislatorName?: string;
    partyName?: string;
    district?: number | string;
    stateName?: string;
    value?: VoteType;
    legislatorImageLink?: string;
  };
  metricTitle?: string;
  metricValue?: number;
  useFeatureLabel?: boolean;
  rank?: number;
}) {
  // Format district label: use "State District N" format if state name is available, otherwise use districtId
  const prettyDistrict = row?.stateName
    ? `${row.stateName} District ${row.partyName ? row.partyName[0] : ''}-${row.district}`
    : districtId;
  const nameRegex = row?.legislatorName?.match(/^(.+?),\s+(\S+)/);
  console.log('parsed leg name: ', nameRegex);
  const firstName = nameRegex ? nameRegex[2].toLowerCase() : '?';
  const lastName = nameRegex ? nameRegex[1].trim().toLowerCase() : '?';

  return (
    <div>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{prettyDistrict}</div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          rowGap: 6,
          columnGap: 12,
          fontSize: 12,
        }}
      >
        <span>Legislator</span>
        <span style={{ fontWeight: 600 }}>
          <a
            target="_blank"
            href={`https://www.congress.gov/member/${firstName}-${lastName}/${row?.legislatorId ?? '-'}`}
          >
            {row?.legislatorName ?? '—'}{' '}
          </a>
        </span>

        {/*<span>Party</span>
        <span style={{ fontWeight: 600 }}>{row?.partyName ?? '—'}</span> */}

        <span>Vote</span>
        <span style={{ fontWeight: 600 }}>{row?.value ? toTitleCase(row.value) : '—'}</span>

        {metricTitle && (
          <>
            <span>{useFeatureLabel ? 'Feature' : metricTitle}</span>
            <span style={{ fontWeight: 600 }}>
              {typeof metricValue === 'number' ? metricValue.toLocaleString('en-US') : '—'}
            </span>
          </>
        )}

        <span>Rank</span>
        <span style={{ fontWeight: 600 }}>{rank}</span>
      </div>
    </div>
  );
}

/**
 * Tooltip component - Main tooltip component for map interactions.
 *
 * This component renders a tooltip that appears when hovering over districts on the map.
 * It subscribes to the viz store for hover state and tooltip position, and the data store
 * for district data and metric information.
 *
 * The tooltip:
 * - Only renders when hover source is 'map' (not 'plot')
 * - Requires a hovered district ID and valid tooltip position
 * - Displays district information including legislator, party, vote, and optional metric
 * - Is positioned fixed relative to the viewport based on mapTooltipPos
 *
 * @returns Tooltip JSX element, or null if conditions aren't met for display
 *
 * @example
 * ```tsx
 * // Tooltip automatically appears when hovering over map districts
 * <Tooltip />
 * ```
 */
export default function Tooltip() {
  const hoveredDistricts = useVizStore((s) => s.hoveredDistricts);
  const hoverSource = useVizStore((s) => s.hoverSource);
  const selectedDistrictId = useVizStore((s) => s.selectedDistrictId);
  const useFeatureLabel = useVizStore((s) => s.useFeatureLabel);
  const selectedDistricts = useVizStore((s) => s.selectedDistricts);
  const mapTooltipPos = useVizStore((s) => s.mapTooltipPos);
  const districts = useDataStore((s) => s.districts);
  const metricKey = useDataStore((s) => s.metricKey);
  const findInMetricIndex = useDataStore((s) => s.findInMetricIndex);

  // Determine which district ID should drive the tooltip:
  // - Prefer a locked selection (selectedDistrictId) when present.
  // - Otherwise, use the first selected district from multi-selection.
  // - Otherwise, fall back to the current map hover (single district).
  const activeDistrictId = useMemo(() => {
    if (selectedDistrictId) return selectedDistrictId;
    if (selectedDistricts.length > 0) return selectedDistricts[0];
    if (hoverSource === 'map' && hoveredDistricts.length > 0) {
      return hoveredDistricts[0];
    }
    return null;
  }, [selectedDistrictId, selectedDistricts, hoverSource, hoveredDistricts]);

  // Find the data row for the active district
  const dataForDistrict = useMemo(() => {
    if (!activeDistrictId) return undefined;
    const data = districts[activeDistrictId as DistrictId];
    if (!data) return undefined;
    // Map DistrictData to the expected format
    return {
      legislatorId: data.legislatorId,
      legislatorName: data.legislatorName,
      partyName: data.legislatorParty,
      district: data.districtNum,
      stateName: data.stateName,
      value: data.voteType,
      legislatorImageLink: data.legislatorImageLink,
    };
  }, [districts, activeDistrictId]);

  // Get metric title, value, and rank for the active district
  const metricInfo = useMemo(() => {
    if (!activeDistrictId || !metricKey)
      return { title: undefined, value: undefined, rank: undefined };
    const data = districts[activeDistrictId as DistrictId];
    if (!data) return { title: undefined, value: undefined, rank: undefined };

    // Get metric title from index
    const metricEntry = findInMetricIndex(metricKey);
    const title = metricEntry?.title;

    // Get metric value from district data
    const value = data.metricValue;

    // Calculate rank (highest to lowest)
    let rank: number | undefined;
    rank = 1;
    if (typeof value === 'number') {
      const allValues = Object.values(districts)
        .map((d) => d.metricValue)
        .filter((val): val is number => typeof val === 'number') //this is just here so Typescript doesn't throw a fit
        .sort((a, b) => b - a);
      rank = allValues.findIndex((val) => val === value) + 1;
    }

    return { title, value, rank };
  }, [activeDistrictId, metricKey, districts, findInMetricIndex]);

  // Do not show tooltip when there is no active district or no tooltip position.
  // Ignore pure plot-hover states (hoverSource === 'plot' with no locked selection).
  // Always show tooltip when districts are selected (locked), regardless of hoverSource.
  const isPlotHoverOnly =
    hoverSource === 'plot' && !selectedDistrictId && selectedDistricts.length === 0;
  const shouldShow =
    selectedDistrictId || selectedDistricts.length > 0
      ? activeDistrictId && mapTooltipPos
      : !isPlotHoverOnly && activeDistrictId && mapTooltipPos;
  if (!shouldShow || !activeDistrictId || !mapTooltipPos) return null;

  return (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        left: mapTooltipPos.x + 12,
        top: mapTooltipPos.y + 12,
        background: 'rgba(255,255,255,0.98)',
        border: '1px solid #ddd',
        padding: '8px 10px',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        pointerEvents: 'auto',
        maxWidth: 320,
        zIndex: 1000,
      }}
    >
      <TooltipContent
        districtId={activeDistrictId}
        row={dataForDistrict}
        metricTitle={metricInfo.title}
        metricValue={metricInfo.value}
        useFeatureLabel={useFeatureLabel}
        rank={metricInfo.rank}
      />
    </div>
  );
}
